import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { StationRole } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputSelect,
    DataSourceLookup,
    InputInt,
    ValidatedInt,
    InputFloat,
    ValidatedFloat,
    InputTimespan,
    DisplayTimespan,
    DisplayInfotext,
    InputBool,
    DisplayBool,
    InputDateTime,
    DateComp,
    InputNullableInt,
    InputNullableFloat,
} from '@/components';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class StationRoleGuiDef extends GuiDef<StationRole> {
    constructor() {
        super('StationRole');

        const EventCommon = storeToRefs(useEventCommonStore());
        const StationsSource = computed(() => {
            return EventCommon.StationsAsSelectLine.value;
        });
        const RacesSource = computed(() => {
            return EventCommon.RacesAsSelectLine.value;
        });

        const Name = new FieldDef<StationRole>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'List';
        Name.TranslationKey = 'stationrole.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (stationrole) => stationrole.Name;
        Name.setValue = (stationrole, value) => (stationrole.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = null;
        Name.SortDirection = undefined;
        Name.cellClassRules = undefined;
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const StationID = new FieldDef<StationRole>();
        StationID.Name = 'StationID';
        StationID.FieldName = 'StationID';
        StationID.DefinedFor = 'List';
        StationID.TranslationKey = 'stationrole.stationid';
        StationID.Editable = true;
        StationID.Filterable = true;
        StationID.Sortable = true;
        StationID.CssClasses = '';
        StationID.value = (stationrole) => stationrole.StationID;
        StationID.setValue = (stationrole, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            stationrole.StationID = value;
        };
        StationID.DetailEditor = markRaw(InputSelect);
        StationID.DetailEditorProps = { datasource: { mapsource: StationsSource } };
        StationID.CellEditor = markRaw(InputSelect);
        StationID.CellEditorProps = { datasource: { mapsource: StationsSource } };
        // @ts-expect-error @ts-ignore
        StationID.CellRenderer = markRaw(DataSourceLookup);
        StationID.CellRendererProps = { datasource: { mapsource: StationsSource } };
        StationID.ExportValueFormatter = (obj: StationRole) => {
            return StationsSource.value.get(obj.StationID)?.text;
        };
        StationID.Validator = null;
        StationID.SortDirection = undefined;
        StationID.cellClassRules = undefined;
        StationID.MinWidth = undefined;
        StationID.MaxWidth = undefined;
        StationID.Tooltip = null;
        this.Fields.push(StationID);

        const RaceID = new FieldDef<StationRole>();
        RaceID.Name = 'RaceID';
        RaceID.FieldName = 'RaceID';
        RaceID.DefinedFor = 'List';
        RaceID.TranslationKey = 'stationrole.raceid';
        RaceID.Editable = true;
        RaceID.Filterable = true;
        RaceID.Sortable = true;
        RaceID.CssClasses = '';
        RaceID.value = (stationrole) => stationrole.RaceID;
        RaceID.setValue = (stationrole, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            stationrole.RaceID = value;
        };
        RaceID.DetailEditor = markRaw(InputSelect);
        RaceID.DetailEditorProps = { datasource: { mapsource: RacesSource } };
        RaceID.CellEditor = markRaw(InputSelect);
        RaceID.CellEditorProps = { datasource: { mapsource: RacesSource } };
        // @ts-expect-error @ts-ignore
        RaceID.CellRenderer = markRaw(DataSourceLookup);
        RaceID.CellRendererProps = { datasource: { mapsource: RacesSource } };
        RaceID.ExportValueFormatter = (obj: StationRole) => {
            return RacesSource.value.get(obj.RaceID)?.text;
        };
        RaceID.Validator = null;
        RaceID.SortDirection = undefined;
        RaceID.cellClassRules = undefined;
        RaceID.MinWidth = undefined;
        RaceID.MaxWidth = undefined;
        RaceID.Tooltip = null;
        this.Fields.push(RaceID);

        const Sort = new FieldDef<StationRole>();
        Sort.Name = 'Sort';
        Sort.FieldName = 'Sort';
        Sort.DefinedFor = 'List';
        Sort.TranslationKey = 'stationrole.sort';
        Sort.Editable = false;
        Sort.Filterable = false;
        Sort.Sortable = true;
        Sort.CssClasses = '';
        Sort.value = (stationrole) => stationrole.Sort;
        Sort.setValue = () => {};
        Sort.DetailEditor = markRaw(InputInt);
        Sort.DetailEditorProps = {};
        Sort.CellEditor = markRaw(InputInt);
        Sort.CellEditorProps = {};
        Sort.CellRenderer = markRaw(ValidatedInt);
        Sort.CellRendererProps = {};
        Sort.Validator = null;
        Sort.SortDirection = undefined;
        Sort.cellClassRules = undefined;
        Sort.MinWidth = undefined;
        Sort.MaxWidth = undefined;
        Sort.Tooltip = null;
        this.Fields.push(Sort);

        const Km = new FieldDef<StationRole>();
        Km.Name = 'Km';
        Km.FieldName = 'Km';
        Km.DefinedFor = 'Both';
        Km.TranslationKey = 'stationrole.km';
        Km.Editable = true;
        Km.Filterable = true;
        Km.Sortable = true;
        Km.CssClasses = '';
        Km.value = (stationrole) => stationrole.Km;
        Km.setValue = (stationrole, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            stationrole.Km = value;
        };
        Km.DetailEditor = markRaw(InputFloat);
        Km.DetailEditorProps = {};
        Km.CellEditor = markRaw(InputFloat);
        Km.CellEditorProps = {};
        Km.CellRenderer = markRaw(ValidatedFloat);
        Km.CellRendererProps = {};
        Km.Validator = null;
        Km.SortDirection = undefined;
        Km.cellClassRules = undefined;
        Km.MinWidth = undefined;
        Km.MaxWidth = undefined;
        Km.Tooltip = null;
        this.Fields.push(Km);

        const MinLapTime = new FieldDef<StationRole>();
        MinLapTime.Group = new GroupDef('row15', '', true);
        MinLapTime.Name = 'MinLapTime';
        MinLapTime.FieldName = 'MinLapTime';
        MinLapTime.DefinedFor = 'Both';
        MinLapTime.TranslationKey = 'stationrole.minlaptime';
        MinLapTime.Editable = true;
        MinLapTime.Filterable = false;
        MinLapTime.Sortable = true;
        MinLapTime.CssClasses = '';
        MinLapTime.value = (stationrole) => stationrole.MinLapTime;
        MinLapTime.setValue = (stationrole, value) => (stationrole.MinLapTime = value);
        MinLapTime.DetailEditor = markRaw(InputTimespan);
        MinLapTime.DetailEditorProps = { format: 'TIME' as DateType };
        MinLapTime.CellEditor = markRaw(InputTimespan);
        MinLapTime.CellEditorProps = { format: 'TIME' as DateType };
        MinLapTime.CellRenderer = markRaw(DisplayTimespan);
        MinLapTime.CellRendererProps = { format: 'TIME' as DateType };
        MinLapTime.Validator = null;
        MinLapTime.SortDirection = undefined;
        MinLapTime.cellClassRules = undefined;
        MinLapTime.MinWidth = 100;
        MinLapTime.MaxWidth = undefined;
        MinLapTime.Tooltip = null;
        this.Fields.push(MinLapTime);

        const infotext0 = new FieldDef<StationRole>();
        infotext0.Group = new GroupDef('row15', '', true);
        infotext0.Name = 'infotext0';
        infotext0.FieldName = 'infotext0';
        infotext0.DefinedFor = 'Detail';
        infotext0.TranslationKey = '';
        infotext0.Editable = true;
        infotext0.Filterable = false;
        infotext0.Sortable = true;
        infotext0.CssClasses = '';
        infotext0.value = () => {
            return '';
        };
        infotext0.setValue = () => {};
        infotext0.DetailEditor = markRaw(DisplayInfotext);
        infotext0.DetailEditorProps = {};
        infotext0.Validator = null;
        infotext0.SortDirection = undefined;
        infotext0.cellClassRules = undefined;
        infotext0.MinWidth = undefined;
        infotext0.MaxWidth = undefined;
        infotext0.Tooltip = null;
        this.Fields.push(infotext0);

        const Start = new FieldDef<StationRole>();
        Start.Group = new GroupDef('row20', '', true);
        Start.Name = 'Start';
        Start.FieldName = 'Start';
        Start.DefinedFor = 'Both';
        Start.TranslationKey = 'stationrole.start';
        Start.Editable = true;
        Start.Filterable = false;
        Start.Sortable = false;
        Start.CssClasses = '';
        Start.value = (stationrole) => stationrole.Start;
        Start.setValue = (stationrole, value) => (stationrole.Start = value);
        Start.DetailEditor = markRaw(InputBool);
        Start.DetailEditorProps = { label: 'stationrole.start' };
        Start.CellEditor = markRaw(InputBool);
        Start.CellEditorProps = {};
        Start.CellRenderer = markRaw(DisplayBool);
        Start.CellRendererProps = {};
        Start.Validator = null;
        Start.SortDirection = undefined;
        Start.cellClassRules = undefined;
        Start.MinWidth = 80;
        Start.MaxWidth = 80;
        Start.Tooltip = null;
        this.Fields.push(Start);

        const Finish = new FieldDef<StationRole>();
        Finish.Group = new GroupDef('row20', '', true);
        Finish.Name = 'Finish';
        Finish.FieldName = 'Finish';
        Finish.DefinedFor = 'Both';
        Finish.TranslationKey = 'stationrole.finish';
        Finish.Editable = true;
        Finish.Filterable = false;
        Finish.Sortable = false;
        Finish.CssClasses = '';
        Finish.value = (stationrole) => stationrole.Finish;
        Finish.setValue = (stationrole, value) => (stationrole.Finish = value);
        Finish.DetailEditor = markRaw(InputBool);
        Finish.DetailEditorProps = { label: 'stationrole.finish' };
        Finish.CellEditor = markRaw(InputBool);
        Finish.CellEditorProps = {};
        Finish.CellRenderer = markRaw(DisplayBool);
        Finish.CellRendererProps = {};
        Finish.Validator = null;
        Finish.SortDirection = undefined;
        Finish.cellClassRules = undefined;
        Finish.MinWidth = 80;
        Finish.MaxWidth = 80;
        Finish.Tooltip = null;
        this.Fields.push(Finish);

        const Public = new FieldDef<StationRole>();
        Public.Group = new GroupDef('row25', '', true);
        Public.Name = 'Public';
        Public.FieldName = 'Public';
        Public.DefinedFor = 'Both';
        Public.TranslationKey = 'stationrole.public';
        Public.Editable = true;
        Public.Filterable = false;
        Public.Sortable = false;
        Public.CssClasses = '';
        Public.value = (stationrole) => stationrole.Public;
        Public.setValue = (stationrole, value) => (stationrole.Public = value);
        Public.DetailEditor = markRaw(InputBool);
        Public.DetailEditorProps = { label: 'stationrole.public' };
        Public.CellEditor = markRaw(InputBool);
        Public.CellEditorProps = {};
        Public.CellRenderer = markRaw(DisplayBool);
        Public.CellRendererProps = {};
        Public.Validator = null;
        Public.SortDirection = undefined;
        Public.cellClassRules = undefined;
        Public.MinWidth = 80;
        Public.MaxWidth = 80;
        Public.Tooltip = null;
        this.Fields.push(Public);

        const Mandatory = new FieldDef<StationRole>();
        Mandatory.Group = new GroupDef('row25', '', true);
        Mandatory.Name = 'Mandatory';
        Mandatory.FieldName = 'Mandatory';
        Mandatory.DefinedFor = 'Both';
        Mandatory.TranslationKey = 'stationrole.mandatory';
        Mandatory.Editable = true;
        Mandatory.Filterable = false;
        Mandatory.Sortable = false;
        Mandatory.CssClasses = '';
        Mandatory.value = (stationrole) => stationrole.Mandatory;
        Mandatory.setValue = (stationrole, value) => (stationrole.Mandatory = value);
        Mandatory.DetailEditor = markRaw(InputBool);
        Mandatory.DetailEditorProps = { label: 'stationrole.mandatory' };
        Mandatory.CellEditor = markRaw(InputBool);
        Mandatory.CellEditorProps = {};
        Mandatory.CellRenderer = markRaw(DisplayBool);
        Mandatory.CellRendererProps = {};
        Mandatory.Validator = null;
        Mandatory.SortDirection = undefined;
        Mandatory.cellClassRules = undefined;
        Mandatory.MinWidth = 80;
        Mandatory.MaxWidth = 80;
        Mandatory.Tooltip = null;
        this.Fields.push(Mandatory);

        const StartTime = new FieldDef<StationRole>();
        StartTime.Group = new GroupDef('row30', '', true);
        StartTime.Name = 'StartTime';
        StartTime.FieldName = 'StartTime';
        StartTime.DefinedFor = 'Both';
        StartTime.TranslationKey = 'stationrole.starttime';
        StartTime.Editable = true;
        StartTime.Filterable = false;
        StartTime.Sortable = true;
        StartTime.CssClasses = '';
        StartTime.value = (stationrole) => stationrole.StartTime;
        StartTime.setValue = (stationrole, value) => (stationrole.StartTime = value);
        StartTime.DetailEditor = markRaw(InputDateTime);
        StartTime.DetailEditorProps = { format: 'DATETIME' as DateType };
        StartTime.CellEditor = markRaw(InputDateTime);
        StartTime.CellEditorProps = { format: 'DATETIME' as DateType };
        StartTime.CellRenderer = markRaw(DateComp);
        StartTime.CellRendererProps = { format: 'DATETIME' as DateType };
        StartTime.Validator = null;
        StartTime.SortDirection = undefined;
        StartTime.cellClassRules = undefined;
        StartTime.MinWidth = 180;
        StartTime.MaxWidth = undefined;
        StartTime.Tooltip = null;
        this.Fields.push(StartTime);

        const StopTime = new FieldDef<StationRole>();
        StopTime.Group = new GroupDef('row30', '', true);
        StopTime.Name = 'StopTime';
        StopTime.FieldName = 'StopTime';
        StopTime.DefinedFor = 'Both';
        StopTime.TranslationKey = 'stationrole.stoptime';
        StopTime.Editable = true;
        StopTime.Filterable = false;
        StopTime.Sortable = true;
        StopTime.CssClasses = '';
        StopTime.value = (stationrole) => stationrole.StopTime;
        StopTime.setValue = (stationrole, value) => (stationrole.StopTime = value);
        StopTime.DetailEditor = markRaw(InputDateTime);
        StopTime.DetailEditorProps = { format: 'DATETIME' as DateType };
        StopTime.CellEditor = markRaw(InputDateTime);
        StopTime.CellEditorProps = { format: 'DATETIME' as DateType };
        StopTime.CellRenderer = markRaw(DateComp);
        StopTime.CellRendererProps = { format: 'DATETIME' as DateType };
        StopTime.Validator = null;
        StopTime.SortDirection = undefined;
        StopTime.cellClassRules = undefined;
        StopTime.MinWidth = 180;
        StopTime.MaxWidth = undefined;
        StopTime.Tooltip = null;
        this.Fields.push(StopTime);

        const IsNeutralZoneIn = new FieldDef<StationRole>();
        IsNeutralZoneIn.Name = 'IsNeutralZoneIn';
        IsNeutralZoneIn.FieldName = 'IsNeutralZoneIn';
        IsNeutralZoneIn.DefinedFor = 'List';
        IsNeutralZoneIn.TranslationKey = 'stationrole.isneutralzonein';
        IsNeutralZoneIn.Editable = true;
        IsNeutralZoneIn.Filterable = false;
        IsNeutralZoneIn.Sortable = true;
        IsNeutralZoneIn.CssClasses = '';
        IsNeutralZoneIn.value = (stationrole) => stationrole.IsNeutralZoneIn;
        IsNeutralZoneIn.setValue = (stationrole, value) => (stationrole.IsNeutralZoneIn = value);
        IsNeutralZoneIn.DetailEditor = markRaw(InputBool);
        IsNeutralZoneIn.DetailEditorProps = { label: 'stationrole.isneutralzonein' };
        IsNeutralZoneIn.CellEditor = markRaw(InputBool);
        IsNeutralZoneIn.CellEditorProps = {};
        IsNeutralZoneIn.CellRenderer = markRaw(DisplayBool);
        IsNeutralZoneIn.CellRendererProps = {};
        IsNeutralZoneIn.Validator = null;
        IsNeutralZoneIn.SortDirection = undefined;
        IsNeutralZoneIn.cellClassRules = undefined;
        IsNeutralZoneIn.MinWidth = undefined;
        IsNeutralZoneIn.MaxWidth = undefined;
        IsNeutralZoneIn.Tooltip = 'stationrole.isneutralzoneintooltip';
        this.Fields.push(IsNeutralZoneIn);

        const IsNeutralZoneOut = new FieldDef<StationRole>();
        IsNeutralZoneOut.Name = 'IsNeutralZoneOut';
        IsNeutralZoneOut.FieldName = 'IsNeutralZoneOut';
        IsNeutralZoneOut.DefinedFor = 'List';
        IsNeutralZoneOut.TranslationKey = 'stationrole.isneutralzoneout';
        IsNeutralZoneOut.Editable = true;
        IsNeutralZoneOut.Filterable = false;
        IsNeutralZoneOut.Sortable = true;
        IsNeutralZoneOut.CssClasses = '';
        IsNeutralZoneOut.value = (stationrole) => stationrole.IsNeutralZoneOut;
        IsNeutralZoneOut.setValue = (stationrole, value) => (stationrole.IsNeutralZoneOut = value);
        IsNeutralZoneOut.DetailEditor = markRaw(InputBool);
        IsNeutralZoneOut.DetailEditorProps = { label: 'stationrole.isneutralzoneout' };
        IsNeutralZoneOut.CellEditor = markRaw(InputBool);
        IsNeutralZoneOut.CellEditorProps = {};
        IsNeutralZoneOut.CellRenderer = markRaw(DisplayBool);
        IsNeutralZoneOut.CellRendererProps = {};
        IsNeutralZoneOut.Validator = null;
        IsNeutralZoneOut.SortDirection = undefined;
        IsNeutralZoneOut.cellClassRules = undefined;
        IsNeutralZoneOut.MinWidth = undefined;
        IsNeutralZoneOut.MaxWidth = undefined;
        IsNeutralZoneOut.Tooltip = 'stationrole.isneutralzoneouttooltip';
        this.Fields.push(IsNeutralZoneOut);

        const StartControl = new FieldDef<StationRole>();
        StartControl.Name = 'StartControl';
        StartControl.FieldName = 'StartControl';
        StartControl.DefinedFor = 'List';
        StartControl.TranslationKey = 'stationrole.startcontrol';
        StartControl.Editable = true;
        StartControl.Filterable = false;
        StartControl.Sortable = true;
        StartControl.CssClasses = '';
        StartControl.value = (stationrole) => stationrole.StartControl;
        StartControl.setValue = (stationrole, value) => (stationrole.StartControl = value);
        StartControl.DetailEditor = markRaw(InputBool);
        StartControl.DetailEditorProps = { label: 'stationrole.startcontrol' };
        StartControl.CellEditor = markRaw(InputBool);
        StartControl.CellEditorProps = {};
        StartControl.CellRenderer = markRaw(DisplayBool);
        StartControl.CellRendererProps = {};
        StartControl.Validator = null;
        StartControl.SortDirection = undefined;
        StartControl.cellClassRules = undefined;
        StartControl.MinWidth = undefined;
        StartControl.MaxWidth = undefined;
        StartControl.Tooltip = null;
        this.Fields.push(StartControl);

        const StartControlMin = new FieldDef<StationRole>();
        StartControlMin.Name = 'StartControlMin';
        StartControlMin.FieldName = 'StartControlMin';
        StartControlMin.DefinedFor = 'List';
        StartControlMin.TranslationKey = 'stationrole.startcontrolmin';
        StartControlMin.Editable = true;
        StartControlMin.Filterable = false;
        StartControlMin.Sortable = true;
        StartControlMin.CssClasses = '';
        StartControlMin.value = (stationrole) => stationrole.StartControlMin;
        StartControlMin.setValue = (stationrole, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            stationrole.StartControlMin = value;
        };
        StartControlMin.DetailEditor = markRaw(InputNullableInt);
        StartControlMin.DetailEditorProps = {};
        StartControlMin.CellEditor = markRaw(InputNullableInt);
        StartControlMin.CellEditorProps = {};
        StartControlMin.CellRenderer = markRaw(ValidatedInt);
        StartControlMin.CellRendererProps = {};
        StartControlMin.Validator = null;
        StartControlMin.SortDirection = undefined;
        StartControlMin.cellClassRules = undefined;
        StartControlMin.MinWidth = undefined;
        StartControlMin.MaxWidth = undefined;
        StartControlMin.Tooltip = null;
        this.Fields.push(StartControlMin);

        const StartControlMax = new FieldDef<StationRole>();
        StartControlMax.Name = 'StartControlMax';
        StartControlMax.FieldName = 'StartControlMax';
        StartControlMax.DefinedFor = 'List';
        StartControlMax.TranslationKey = 'stationrole.startcontrolmax';
        StartControlMax.Editable = true;
        StartControlMax.Filterable = false;
        StartControlMax.Sortable = true;
        StartControlMax.CssClasses = '';
        StartControlMax.value = (stationrole) => stationrole.StartControlMax;
        StartControlMax.setValue = (stationrole, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            stationrole.StartControlMax = value;
        };
        StartControlMax.DetailEditor = markRaw(InputNullableInt);
        StartControlMax.DetailEditorProps = {};
        StartControlMax.CellEditor = markRaw(InputNullableInt);
        StartControlMax.CellEditorProps = {};
        StartControlMax.CellRenderer = markRaw(ValidatedInt);
        StartControlMax.CellRendererProps = {};
        StartControlMax.Validator = null;
        StartControlMax.SortDirection = undefined;
        StartControlMax.cellClassRules = undefined;
        StartControlMax.MinWidth = undefined;
        StartControlMax.MaxWidth = undefined;
        StartControlMax.Tooltip = null;
        this.Fields.push(StartControlMax);

        const RelayLegnumber = new FieldDef<StationRole>();
        RelayLegnumber.Name = 'RelayLegnumber';
        RelayLegnumber.FieldName = 'RelayLegnumber';
        RelayLegnumber.DefinedFor = 'List';
        RelayLegnumber.TranslationKey = 'stationrole.relaylegnumber';
        RelayLegnumber.Editable = true;
        RelayLegnumber.Filterable = false;
        RelayLegnumber.Sortable = true;
        RelayLegnumber.CssClasses = '';
        RelayLegnumber.value = (stationrole) => stationrole.RelayLegnumber;
        RelayLegnumber.setValue = (stationrole, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            stationrole.RelayLegnumber = value;
        };
        RelayLegnumber.DetailEditor = markRaw(InputNullableInt);
        RelayLegnumber.DetailEditorProps = {};
        RelayLegnumber.CellEditor = markRaw(InputNullableInt);
        RelayLegnumber.CellEditorProps = {};
        RelayLegnumber.CellRenderer = markRaw(ValidatedInt);
        RelayLegnumber.CellRendererProps = {};
        RelayLegnumber.Validator = null;
        RelayLegnumber.SortDirection = undefined;
        RelayLegnumber.cellClassRules = undefined;
        RelayLegnumber.MinWidth = undefined;
        RelayLegnumber.MaxWidth = undefined;
        RelayLegnumber.Tooltip = null;
        this.Fields.push(RelayLegnumber);

        const AcceptChipForRelayLegnumber = new FieldDef<StationRole>();
        AcceptChipForRelayLegnumber.Name = 'AcceptChipForRelayLegnumber';
        AcceptChipForRelayLegnumber.FieldName = 'AcceptChipForRelayLegnumber';
        AcceptChipForRelayLegnumber.DefinedFor = 'List';
        AcceptChipForRelayLegnumber.TranslationKey = 'stationrole.acceptchipforrelaylegnumber';
        AcceptChipForRelayLegnumber.Editable = true;
        AcceptChipForRelayLegnumber.Filterable = false;
        AcceptChipForRelayLegnumber.Sortable = true;
        AcceptChipForRelayLegnumber.CssClasses = '';
        AcceptChipForRelayLegnumber.value = (stationrole) => stationrole.AcceptChipForRelayLegnumber;
        AcceptChipForRelayLegnumber.setValue = (stationrole, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            stationrole.AcceptChipForRelayLegnumber = value;
        };
        AcceptChipForRelayLegnumber.DetailEditor = markRaw(InputNullableInt);
        AcceptChipForRelayLegnumber.DetailEditorProps = {};
        AcceptChipForRelayLegnumber.CellEditor = markRaw(InputNullableInt);
        AcceptChipForRelayLegnumber.CellEditorProps = {};
        AcceptChipForRelayLegnumber.CellRenderer = markRaw(ValidatedInt);
        AcceptChipForRelayLegnumber.CellRendererProps = {};
        AcceptChipForRelayLegnumber.Validator = null;
        AcceptChipForRelayLegnumber.SortDirection = undefined;
        AcceptChipForRelayLegnumber.cellClassRules = undefined;
        AcceptChipForRelayLegnumber.MinWidth = undefined;
        AcceptChipForRelayLegnumber.MaxWidth = undefined;
        AcceptChipForRelayLegnumber.Tooltip = null;
        this.Fields.push(AcceptChipForRelayLegnumber);

        const PrintOnResultlist = new FieldDef<StationRole>();
        PrintOnResultlist.Name = 'PrintOnResultlist';
        PrintOnResultlist.FieldName = 'PrintOnResultlist';
        PrintOnResultlist.DefinedFor = 'List';
        PrintOnResultlist.TranslationKey = 'stationrole.printonresultlist';
        PrintOnResultlist.Editable = true;
        PrintOnResultlist.Filterable = false;
        PrintOnResultlist.Sortable = true;
        PrintOnResultlist.CssClasses = '';
        PrintOnResultlist.value = (stationrole) => stationrole.PrintOnResultlist;
        PrintOnResultlist.setValue = (stationrole, value) => (stationrole.PrintOnResultlist = value);
        PrintOnResultlist.DetailEditor = markRaw(InputBool);
        PrintOnResultlist.DetailEditorProps = { label: 'stationrole.printonresultlist' };
        PrintOnResultlist.CellEditor = markRaw(InputBool);
        PrintOnResultlist.CellEditorProps = {};
        PrintOnResultlist.CellRenderer = markRaw(DisplayBool);
        PrintOnResultlist.CellRendererProps = {};
        PrintOnResultlist.Validator = null;
        PrintOnResultlist.SortDirection = undefined;
        PrintOnResultlist.cellClassRules = undefined;
        PrintOnResultlist.MinWidth = undefined;
        PrintOnResultlist.MaxWidth = undefined;
        PrintOnResultlist.Tooltip = 'stationrole.printonresultlisttooltip';
        this.Fields.push(PrintOnResultlist);

        const IgnoreInSplitresult = new FieldDef<StationRole>();
        IgnoreInSplitresult.Name = 'IgnoreInSplitresult';
        IgnoreInSplitresult.FieldName = 'IgnoreInSplitresult';
        IgnoreInSplitresult.DefinedFor = 'List';
        IgnoreInSplitresult.TranslationKey = 'stationrole.ignoreinsplitresult';
        IgnoreInSplitresult.Editable = true;
        IgnoreInSplitresult.Filterable = false;
        IgnoreInSplitresult.Sortable = true;
        IgnoreInSplitresult.CssClasses = '';
        IgnoreInSplitresult.value = (stationrole) => stationrole.IgnoreInSplitresult;
        IgnoreInSplitresult.setValue = (stationrole, value) => (stationrole.IgnoreInSplitresult = value);
        IgnoreInSplitresult.DetailEditor = markRaw(InputBool);
        IgnoreInSplitresult.DetailEditorProps = { label: 'stationrole.ignoreinsplitresult' };
        IgnoreInSplitresult.CellEditor = markRaw(InputBool);
        IgnoreInSplitresult.CellEditorProps = {};
        IgnoreInSplitresult.CellRenderer = markRaw(DisplayBool);
        IgnoreInSplitresult.CellRendererProps = {};
        IgnoreInSplitresult.Validator = null;
        IgnoreInSplitresult.SortDirection = undefined;
        IgnoreInSplitresult.cellClassRules = undefined;
        IgnoreInSplitresult.MinWidth = undefined;
        IgnoreInSplitresult.MaxWidth = undefined;
        IgnoreInSplitresult.Tooltip = 'stationrole.ignoreinsplitresulttooltip';
        this.Fields.push(IgnoreInSplitresult);

        const PhotofinishMilliseconds = new FieldDef<StationRole>();
        PhotofinishMilliseconds.Name = 'PhotofinishMilliseconds';
        PhotofinishMilliseconds.FieldName = 'PhotofinishMilliseconds';
        PhotofinishMilliseconds.DefinedFor = 'List';
        PhotofinishMilliseconds.TranslationKey = 'stationrole.photofinishmilliseconds';
        PhotofinishMilliseconds.Editable = true;
        PhotofinishMilliseconds.Filterable = false;
        PhotofinishMilliseconds.Sortable = true;
        PhotofinishMilliseconds.CssClasses = '';
        PhotofinishMilliseconds.value = (stationrole) => stationrole.PhotofinishMilliseconds;
        PhotofinishMilliseconds.setValue = (stationrole, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            stationrole.PhotofinishMilliseconds = value;
        };
        PhotofinishMilliseconds.DetailEditor = markRaw(InputNullableInt);
        PhotofinishMilliseconds.DetailEditorProps = {};
        PhotofinishMilliseconds.CellEditor = markRaw(InputNullableInt);
        PhotofinishMilliseconds.CellEditorProps = {};
        PhotofinishMilliseconds.CellRenderer = markRaw(ValidatedInt);
        PhotofinishMilliseconds.CellRendererProps = {};
        PhotofinishMilliseconds.Validator = null;
        PhotofinishMilliseconds.SortDirection = undefined;
        PhotofinishMilliseconds.cellClassRules = undefined;
        PhotofinishMilliseconds.MinWidth = undefined;
        PhotofinishMilliseconds.MaxWidth = undefined;
        PhotofinishMilliseconds.Tooltip = 'stationrole.photofinishtooltip';
        this.Fields.push(PhotofinishMilliseconds);

        const SmoothingInSeconds = new FieldDef<StationRole>();
        SmoothingInSeconds.Name = 'SmoothingInSeconds';
        SmoothingInSeconds.FieldName = 'SmoothingInSeconds';
        SmoothingInSeconds.DefinedFor = 'List';
        SmoothingInSeconds.TranslationKey = 'stationrole.smoothinginseconds';
        SmoothingInSeconds.Editable = true;
        SmoothingInSeconds.Filterable = false;
        SmoothingInSeconds.Sortable = true;
        SmoothingInSeconds.CssClasses = '';
        SmoothingInSeconds.value = (stationrole) => stationrole.SmoothingInSeconds;
        SmoothingInSeconds.setValue = (stationrole, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            stationrole.SmoothingInSeconds = value;
        };
        SmoothingInSeconds.DetailEditor = markRaw(InputInt);
        SmoothingInSeconds.DetailEditorProps = {};
        SmoothingInSeconds.CellEditor = markRaw(InputInt);
        SmoothingInSeconds.CellEditorProps = {};
        SmoothingInSeconds.CellRenderer = markRaw(ValidatedInt);
        SmoothingInSeconds.CellRendererProps = {};
        SmoothingInSeconds.Validator = null;
        SmoothingInSeconds.SortDirection = undefined;
        SmoothingInSeconds.cellClassRules = undefined;
        SmoothingInSeconds.MinWidth = undefined;
        SmoothingInSeconds.MaxWidth = undefined;
        SmoothingInSeconds.Tooltip = 'stationrole.smoothinginsecondstooltip';
        this.Fields.push(SmoothingInSeconds);

        const AssignLapPoints = new FieldDef<StationRole>();
        AssignLapPoints.Name = 'AssignLapPoints';
        AssignLapPoints.FieldName = 'AssignLapPoints';
        AssignLapPoints.DefinedFor = 'List';
        AssignLapPoints.TranslationKey = 'stationrole.assignlappoints';
        AssignLapPoints.Editable = true;
        AssignLapPoints.Filterable = false;
        AssignLapPoints.Sortable = true;
        AssignLapPoints.CssClasses = '';
        AssignLapPoints.value = (stationrole) => stationrole.AssignLapPoints;
        AssignLapPoints.setValue = (stationrole, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            stationrole.AssignLapPoints = value;
        };
        AssignLapPoints.DetailEditor = markRaw(InputNullableFloat);
        AssignLapPoints.DetailEditorProps = {};
        AssignLapPoints.CellEditor = markRaw(InputNullableFloat);
        AssignLapPoints.CellEditorProps = {};
        AssignLapPoints.CellRenderer = markRaw(ValidatedFloat);
        AssignLapPoints.CellRendererProps = {};
        AssignLapPoints.Validator = null;
        AssignLapPoints.SortDirection = undefined;
        AssignLapPoints.cellClassRules = undefined;
        AssignLapPoints.MinWidth = undefined;
        AssignLapPoints.MaxWidth = undefined;
        AssignLapPoints.Tooltip = 'stationrole.assignlappointstooltip';
        this.Fields.push(AssignLapPoints);

        const Pretiming = new FieldDef<StationRole>();
        Pretiming.Name = 'Pretiming';
        Pretiming.FieldName = 'Pretiming';
        Pretiming.DefinedFor = 'List';
        Pretiming.TranslationKey = 'stationrole.pretiming';
        Pretiming.Editable = true;
        Pretiming.Filterable = false;
        Pretiming.Sortable = true;
        Pretiming.CssClasses = '';
        Pretiming.value = (stationrole) => stationrole.Pretiming;
        Pretiming.setValue = (stationrole, value) => (stationrole.Pretiming = value);
        Pretiming.DetailEditor = markRaw(InputBool);
        Pretiming.DetailEditorProps = { label: 'stationrole.pretiming' };
        Pretiming.CellEditor = markRaw(InputBool);
        Pretiming.CellEditorProps = {};
        Pretiming.CellRenderer = markRaw(DisplayBool);
        Pretiming.CellRendererProps = {};
        Pretiming.Validator = null;
        Pretiming.SortDirection = undefined;
        Pretiming.cellClassRules = undefined;
        Pretiming.MinWidth = undefined;
        Pretiming.MaxWidth = undefined;
        Pretiming.Tooltip = 'stationrole.pretimingtooltip';
        this.Fields.push(Pretiming);

        const AutomaticallyChangeRace = new FieldDef<StationRole>();
        AutomaticallyChangeRace.Name = 'AutomaticallyChangeRace';
        AutomaticallyChangeRace.FieldName = 'AutomaticallyChangeRace';
        AutomaticallyChangeRace.DefinedFor = 'List';
        AutomaticallyChangeRace.TranslationKey = 'stationrole.automaticallychangerace';
        AutomaticallyChangeRace.Editable = true;
        AutomaticallyChangeRace.Filterable = false;
        AutomaticallyChangeRace.Sortable = true;
        AutomaticallyChangeRace.CssClasses = '';
        AutomaticallyChangeRace.value = (stationrole) => stationrole.AutomaticallyChangeRace;
        AutomaticallyChangeRace.setValue = (stationrole, value) => (stationrole.AutomaticallyChangeRace = value);
        AutomaticallyChangeRace.DetailEditor = markRaw(InputBool);
        AutomaticallyChangeRace.DetailEditorProps = { label: 'stationrole.automaticallychangerace' };
        AutomaticallyChangeRace.CellEditor = markRaw(InputBool);
        AutomaticallyChangeRace.CellEditorProps = {};
        AutomaticallyChangeRace.CellRenderer = markRaw(DisplayBool);
        AutomaticallyChangeRace.CellRendererProps = {};
        AutomaticallyChangeRace.Validator = null;
        AutomaticallyChangeRace.SortDirection = undefined;
        AutomaticallyChangeRace.cellClassRules = undefined;
        AutomaticallyChangeRace.MinWidth = undefined;
        AutomaticallyChangeRace.MaxWidth = undefined;
        AutomaticallyChangeRace.Tooltip = 'stationrole.automaticallychangeracetooltip';
        this.Fields.push(AutomaticallyChangeRace);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Stations']);
        await useEventCommonStore().EnsureLoaded(['Races']);
    }
}
